import { processListingParams } from '@/api/helpers';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { getExceptionFromAxiosError } from '../helpers';
import { MessageResponseModel } from '../models';
import {
  AccountDomain,
  AccountDomainsResponse,
  AddDomainRequest,
  SendVerificationEmailRequest,
} from './types/account-domain.type';

export const accountDomainsService = (client: AxiosInstance) => {
  const BASE_URL = (accountId: string) => `/accounts/${accountId}/domains`;

  const getAccountDomains = async (
    accountId: string,
    params: Record<string, unknown> = {},
  ): Promise<AccountDomainsResponse> => {
    try {
      const result = await client.get<
        AccountDomainsResponse,
        AxiosResponse<AccountDomainsResponse>
      >(BASE_URL(accountId), {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const addDomain = async (
    accountId: string,
    request: AddDomainRequest,
  ): Promise<AccountDomain> => {
    try {
      const result = await client.post<
        AccountDomain,
        AxiosResponse<AccountDomain>
      >(BASE_URL(accountId), request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const sendVerificationEmail = async (
    accountId: string,
    domainId: string,
    request: SendVerificationEmailRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${BASE_URL(accountId)}/${domainId}/verify`, request, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const removeDomain = async (
    accountId: string,
    domainId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${BASE_URL(accountId)}/${domainId}`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const forceVerifyDomain = async (
    accountId: string,
    domainId: string,
  ): Promise<AccountDomain> => {
    try {
      const result = await client.post<
        AccountDomain,
        AxiosResponse<AccountDomain>
      >(
        `${BASE_URL(accountId)}/${domainId}/force-verify`,
        {},
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const confirmDomainVerification = async (
    token: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        '/domains/verify',
        { token },
        {
          withCredentials: true,
        },
      );

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getAccountDomains,
    addDomain,
    forceVerifyDomain,
    sendVerificationEmail,
    removeDomain,
    confirmDomainVerification,
  };
};
